import type React from "react";
import { LinkPreview } from "@/components/common/LinkPreview";
import { useTranslation } from "react-i18next";
import type { StreamPreviewItem } from "@/lib/utils/streamPreviewUrl";
import { FilePreview } from "./FilePreview";

interface PreviewSectionProps {
  previewItem: StreamPreviewItem | null;
  fileGuids: Array<{ fileName: string; guid: string }>;
}

export const PreviewSection: React.FC<PreviewSectionProps> = ({
  previewItem,
  fileGuids,
}) => {
  const { t } = useTranslation();
  const hasContent = previewItem !== null || fileGuids.length > 0;
  const visibleFileGuids = fileGuids.slice(0, 2);
  const hiddenFileGuidCount = fileGuids.length - visibleFileGuids.length;

  if (!hasContent) return null;

  const renderPreviewItem = (item: StreamPreviewItem) => {
    if (item.type === "post") {
      return (
        <LinkPreview
          url={item.url}
          titleOverride={t("common.post", {
            defaultValue: "Post",
          })}
        />
      );
    }

    if (item.type === "files") {
      return (
        <LinkPreview
          url={item.url}
          titleOverride={
            item.fileId
              ? t("common.fileTypeFile", {
                  defaultValue: "File",
                })
              : t("common.folder", {
                  defaultValue: "Folder",
                })
          }
        />
      );
    }

    if (item.type === "studyGroup") {
      return (
        <LinkPreview
          url={item.url}
          titleOverride={t("common.studyGroup", {
            defaultValue: "Study Group",
          })}
        />
      );
    }

    if (item.type === "chat") {
      return (
        <LinkPreview
          url={item.url}
          titleOverride={t("common.chat", {
            defaultValue: "Chat",
          })}
        />
      );
    }

    return <LinkPreview url={item.url} />;
  };

  return (
    <div className="mt-3 space-y-2">
      {previewItem && renderPreviewItem(previewItem)}
      {visibleFileGuids.map((item) => (
        <FilePreview
          key={item.guid}
          guid={item.guid}
          fileName={item.fileName}
        />
      ))}
      {hiddenFileGuidCount > 0 && (
        <p className="text-xs font-medium text-slate-500">
          {t("common.moreLinkedFiles", {
            count: hiddenFileGuidCount,
            defaultValue: `+${hiddenFileGuidCount} linked files`,
          })}
        </p>
      )}
    </div>
  );
};
